import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'sonner'
import { Cpu, Loader2, Mic, MicOff, Paperclip, Search, Send, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { SkillInstalled } from '@/features/skills/skills-api'
import type { SkillInvocation } from './use-chat'
import { fuzzyFilter, SlashPopover } from './slash-popover'
import { uploadAttachment, type AttachmentRef } from './attachments-api'
import { useVoiceRecorder } from './voice-recorder'
import { AttachmentPill } from './attachment-pill'
import { ChatActionMenu } from './action-menu'
import { parseWikiCommand, type WikiInvocation } from './parse-wiki-command'
import {
  parsePlatformOutputCommand,
  type PlatformOutputInvocation,
  type PlatformSkill,
} from './parse-platform-output-command'
import { normalizeInlineCommand } from './inline-command'
import { detectSkillIntent, type SuggestableSkill } from './skill-intent'

export { parseWikiCommand, type WikiInvocation } from './parse-wiki-command'
export {
  parsePlatformOutputCommand,
  type PlatformMode,
  type PlatformOutputInvocation,
  type PlatformSkill,
} from './parse-platform-output-command'
import { cn } from '@/lib/utils'
import { KNOWN_CHAT_MODELS } from '@mindkeep/shared'

// Builtin-Skills tauchen im Slash-Popover auf, sind aber NICHT in skills_installed.
// Das Backend kennt sie als Sonder-Endpoints (z.B. /api/research, /api/wiki).
function builtin(name: string, description: string): SkillInstalled {
  return {
    id: `builtin-${name}`,
    workspace_id: 'builtin',
    skill_name: name,
    version: 'builtin',
    source_repo: 'builtin',
    source_commit: null,
    description,
    metadata: { builtin: true },
    enabled: true,
    installed_by: null,
    installed_at: '1970-01-01T00:00:00.000Z',
    updated_at: '1970-01-01T00:00:00.000Z',
  }
}

const BUILTIN_SLASH_COMMANDS: SkillInstalled[] = [
  builtin('research', 'Recherche im Web zu einem Thema durchführen'),
  builtin('memory', 'Wissen speichern, durchsuchen und pflegen'),
  builtin('linkedin', 'LinkedIn-Beitrag aus deinem Wissen erstellen'),
  builtin('x', 'X-Beitrag aus deinem Wissen erstellen'),
  builtin('newsletter', 'Newsletter aus deinem Wissen erstellen'),
  builtin('blog', 'Blog-Artikel aus deinem Wissen erstellen'),
  builtin('presentation', 'Präsentation aus deinem Wissen erstellen'),
  builtin('data-analysis', 'Daten analysieren und auswerten'),
  builtin('image', 'Bild aus einer Beschreibung generieren'),
]

export interface ImageInvocation {
  prompt: string
  style?: 'infografik' | 'whiteboard' | 'illustration' | 'hero'
}

const KNOWN_STYLES = new Set(['infografik', 'whiteboard', 'illustration', 'hero'] as const)
const KNOWN_DISCOVERY_PROVIDERS = new Set(['tavily', 'gemini', 'grok', 'parallel'] as const)

export interface ResearchInvocation {
  topic: string
  discovery?: 'tavily' | 'gemini' | 'grok' | 'parallel'
}

function parseResearchCommand(rest: string): ResearchInvocation | null {
  let raw = rest.trim()
  if (!raw) return null

  let discovery: ResearchInvocation['discovery']
  const flagMatch = raw.match(/(?:^|\s)--discovery(?:=|\s+)(tavily|gemini|grok|parallel)\b/i)
  if (flagMatch?.[1]) {
    const value = flagMatch[1].toLowerCase()
    if ((KNOWN_DISCOVERY_PROVIDERS as ReadonlySet<string>).has(value)) {
      discovery = value as ResearchInvocation['discovery']
    }
    raw = raw.replace(flagMatch[0], ' ').trim()
  }

  const topic = raw.replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').trim()
  if (topic.length < 2) return null
  return { topic, discovery }
}

function parseImageCommand(rest: string): ImageInvocation | null {
  const trimmed = rest.trim()
  if (!trimmed) return null
  // Extract optional --style flag
  const styleMatch = trimmed.match(/--style\s+(\w+)/)
  const promptText = trimmed.replace(/--style\s+\w+/, '').trim()
  if (!promptText) return null
  let style: ImageInvocation['style']
  if (styleMatch && styleMatch[1] && (KNOWN_STYLES as ReadonlySet<string>).has(styleMatch[1].toLowerCase())) {
    style = styleMatch[1].toLowerCase() as ImageInvocation['style']
  }
  return { prompt: promptText, style }
}

const PLATFORM_COMMANDS = new Set<PlatformSkill>([
  'linkedin',
  'x',
  'newsletter',
  'blog',
  'presentation',
  'data-analysis',
])



interface ChatInputProps {
  model: string
  disabled: boolean
  isStreaming: boolean
  sessionId: string | null
  installedSkills: SkillInstalled[]
  prefill?: string
  isUpdatingModel?: boolean
  allowedModels?: string[]
  onSend: (content: string, attachments?: AttachmentRef[], options?: { searchVault?: boolean }) => void
  onInvokeSkill: (invocation: SkillInvocation) => void
  onResearch?: (invocation: ResearchInvocation, rawCommand: string) => void
  onWiki?: (invocation: WikiInvocation, rawCommand: string) => void
  onPlatformOutput?: (invocation: PlatformOutputInvocation, rawCommand: string) => void
  onImage?: (invocation: ImageInvocation, rawCommand: string) => void
  onModelChange?: (model: string) => void
}

export function ChatInput({
  model,
  disabled,
  isStreaming,
  sessionId,
  installedSkills,
  prefill,
  onSend,
  onInvokeSkill,
  onResearch,
  onWiki,
  onPlatformOutput,
  onImage,
  onModelChange,
  isUpdatingModel,
  allowedModels,
}: ChatInputProps) {
  const [value, setValue] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [popoverManuallyClosed, setPopoverManuallyClosed] = useState(false)
  const [pendingAttachments, setPendingAttachments] = useState<AttachmentRef[]>([])
  const [isUploading, setIsUploading] = useState(false)
  // M2-B: Vault-Kontext ist per Default an (EDIH Wissen-first) — User kann pro Nachricht deaktivieren.
  const [searchVault, setSearchVault] = useState(true)
  // M5-B: PII-Bestaetigung als zugaenglicher Dialog statt window.confirm —
  // haelt die erkannten Typen, solange die Entscheidung offen ist.
  const [piiConfirmTypes, setPiiConfirmTypes] = useState<string[] | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)
  const voice = useVoiceRecorder()

  useEffect(() => {
    if (!prefill) return
    setValue(prefill)
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      el.setSelectionRange(prefill.length, prefill.length)
    })
  }, [prefill])

  // Skills + Builtins zusammenfuehren — Builtins zuerst, damit /research priorisiert ist.
  const popoverItems = useMemo(
    () => [...BUILTIN_SLASH_COMMANDS, ...installedSkills],
    [installedSkills],
  )

  // Bekannte Befehlsnamen fuer die Inline-Erkennung (A): Builtin-Namen
  // (inkl. wiki und memory) + installierte skill_name.
  const knownCommandNames = useMemo(
    () =>
      new Set<string>([
        ...BUILTIN_SLASH_COMMANDS.map((s) => s.skill_name),
        'wiki',
        ...installedSkills.map((s) => s.skill_name),
      ]),
    [installedSkills],
  )

  // B) Skill-Vorschlag fuer natuerliche Sprache — nur Hinweis, nie Auto-Ausfuehrung.
  const [dismissedSuggestionFor, setDismissedSuggestionFor] = useState<string | null>(null)

  // Auto-Resize bis 8 Zeilen
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const lineHeight = 22
    const maxHeight = lineHeight * 8 + 16
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
  }, [value])

  // Popover wird angezeigt, sobald die Eingabe mit "/" beginnt — und solange der Cursor
  // im ersten "Wort" steht (also vor dem ersten Leerzeichen).
  const slashState = useMemo(() => parseSlashState(value), [value])
  const slashQuery = slashState.kind === 'in_skill_name' ? slashState.query : ''
  const skillSuggestion = useMemo(() => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === dismissedSuggestionFor) return null
    if (trimmed.startsWith('/')) return null
    if (normalizeInlineCommand(trimmed, knownCommandNames) !== trimmed) return null
    const intent = detectSkillIntent(trimmed)
    if (!intent) return null
    const available: Record<SuggestableSkill, boolean> = {
      research: !!onResearch,
      image: !!onImage,
      presentation: !!onPlatformOutput,
      linkedin: !!onPlatformOutput,
      x: !!onPlatformOutput,
      newsletter: !!onPlatformOutput,
      blog: !!onPlatformOutput,
      'data-analysis': !!onPlatformOutput,
    }
    return available[intent.skill] ? intent : null
  }, [value, dismissedSuggestionFor, knownCommandNames, onResearch, onImage, onPlatformOutput])

  const showPopover =
    !popoverManuallyClosed &&
    !isStreaming &&
    slashState.kind === 'in_skill_name' &&
    popoverItems.length > 0

  const filteredSkills = useMemo(
    () => fuzzyFilter(popoverItems, slashQuery),
    [popoverItems, slashQuery],
  )

  // Bei jedem Tippwechsel den Index zuruecksetzen
  useEffect(() => {
    setActiveIndex(0)
  }, [slashQuery])

  // Wenn der User wieder etwas tippt und das vorher manuell-geschlossene Popover
  // den slash-state verliert (z.B. Loeschen), wieder aktivieren.
  useEffect(() => {
    if (slashState.kind !== 'in_skill_name') {
      setPopoverManuallyClosed(false)
    }
  }, [slashState.kind])

  const applySkillSelection = useCallback(
    (skillName: string) => {
      // Praefix einfuegen, Cursor hinter den Skill-Namen + Leerzeichen setzen
      const next = `/${skillName} `
      setValue(next)
      requestAnimationFrame(() => {
        const el = ref.current
        if (!el) return
        el.focus()
        el.setSelectionRange(next.length, next.length)
      })
    },
    [],
  )

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!sessionId || files.length === 0) return
      setIsUploading(true)
      try {
        for (const file of files) {
          try {
            const ref = await uploadAttachment(sessionId, file)
            setPendingAttachments((prev) => [...prev, ref])
            // Parser-Fehler (z.B. Audio-Transkription fehlgeschlagen/policy-blockiert)
            // kommen als 201 mit parserError — als Toast sichtbar machen, nicht
            // stillschweigend als leeres Attachment behandeln.
            if (ref.parserError) {
              toast.error(`${file.name}: ${ref.parserError}`)
            }
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Upload fehlgeschlagen'
            toast.error(`${file.name}: ${msg}`)
          }
        }
      } finally {
        setIsUploading(false)
      }
    },
    [sessionId],
  )

  const dropzone = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (files) => void handleFiles(files),
  })

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const handleVoiceToggle = async () => {
    if (!sessionId) {
      toast.error('Voice-Aufnahme braucht eine aktive Session.')
      return
    }
    if (!voice.isSupported) {
      toast.error('Browser unterstuetzt MediaRecorder nicht.')
      return
    }
    if (voice.isRecording) {
      const file = await voice.stop()
      if (file) await handleFiles([file])
    } else {
      try {
        await voice.start()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Mikrofon-Zugriff verweigert')
      }
    }
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()
    const trimmed = value.trim()
    if (disabled || isStreaming) return
    // Wenn Attachments vorhanden, ist Text optional (LLM bekommt extracted_text/transcripts)
    if (!trimmed && pendingAttachments.length === 0) return
    const piiTypes = detectLikelyPii(trimmed)
    if (piiTypes.length > 0) {
      toast.warning(`Moegliche PII erkannt: ${piiTypes.join(', ')}`)
      setPiiConfirmTypes(piiTypes)
      return
    }
    proceedWithSend(trimmed)
  }

  const applySkillSuggestion = (skill: SuggestableSkill) => {
    const trimmed = value.trim()
    if (disabled || isStreaming || !trimmed) return
    const command = `/${skill} ${trimmed}`
    // PII-Pruefung wie bei submit: der Dialog sendet value.trim(), daher den
    // Befehl vorher ins Eingabefeld uebernehmen.
    const piiTypes = detectLikelyPii(trimmed)
    if (piiTypes.length > 0) {
      setValue(command)
      toast.warning(`Moegliche PII erkannt: ${piiTypes.join(', ')}`)
      setPiiConfirmTypes(piiTypes)
      return
    }
    proceedWithSend(command)
  }

  const proceedWithSend = (raw: string) => {
    // A) Inline-Befehl: erstes "/name"-Token mitten im Text auf Textanfang heben.
    const trimmed = normalizeInlineCommand(raw, knownCommandNames)
    // 1) Builtin: /research <topic>
    if (onResearch && trimmed.startsWith('/research')) {
      const invocation = parseResearchCommand(trimmed.slice('/research'.length))
      if (invocation) {
        onResearch(invocation, trimmed)
        setValue('')
        setPopoverManuallyClosed(false)
        return
      }
    }

    // 2) Builtin: /wiki <op> [args] — akzeptiert auch /memory als Alias (B1.6)
    const wikiPrefix = trimmed.startsWith('/memory')
      ? '/memory'
      : trimmed.startsWith('/wiki')
        ? '/wiki'
        : null
    if (onWiki && wikiPrefix) {
      const wikiCall = parseWikiCommand(trimmed.slice(wikiPrefix.length))
      if (wikiCall) {
        onWiki(wikiCall, trimmed)
        setValue('')
        setPopoverManuallyClosed(false)
        return
      }
    }

    // 3) Builtin platform outputs: /linkedin, /x, /newsletter, /blog, /presentation, /data-analysis
    if (onPlatformOutput && trimmed.startsWith('/')) {
      const state = parseSlashState(trimmed)
      if (state.kind === 'after_skill_name' && PLATFORM_COMMANDS.has(state.skillName as PlatformSkill)) {
        const outputCall = parsePlatformOutputCommand(state.skillName as PlatformSkill, state.rest)
        if (outputCall) {
          onPlatformOutput(outputCall, trimmed)
          setValue('')
          setPopoverManuallyClosed(false)
          return
        }
      }
      if (state.kind === 'in_skill_name' && PLATFORM_COMMANDS.has(state.query as PlatformSkill)) {
        return
      }
    }

    // 4) Builtin: /image <prompt> [--style …]
    if (onImage && trimmed.startsWith('/image')) {
      const imageCall = parseImageCommand(trimmed.slice('/image'.length))
      if (imageCall) {
        onImage(imageCall, trimmed)
        setValue('')
        setPopoverManuallyClosed(false)
        return
      }
    }

    // 5) Skill-Invocation: "/<skill_name> [operation] [args]"
    const skillCall = parseSkillCall(trimmed, installedSkills)
    if (skillCall) {
      onInvokeSkill(skillCall)
    } else {
      onSend(
        trimmed || '(leer)',
        pendingAttachments.length > 0 ? pendingAttachments : undefined,
        { searchVault },
      )
    }
    setValue('')
    setPendingAttachments([])
    setSearchVault(true)
    setPopoverManuallyClosed(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showPopover && filteredSkills.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % filteredSkills.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + filteredSkills.length) % filteredSkills.length)
        return
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !(e.metaKey || e.ctrlKey) && !e.shiftKey)) {
        e.preventDefault()
        const skill = filteredSkills[activeIndex]
        if (skill) applySkillSelection(skill.skill_name)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setPopoverManuallyClosed(true)
        return
      }
    }

    // Send: Enter (plain ODER Cmd/Ctrl+Enter als Alias). Newline: Shift+Enter.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    <form
      onSubmit={submit}
      {...dropzone.getRootProps({
        className: cn(
          'relative border-t border-border bg-background',
          dropzone.isDragActive && 'bg-muted/40',
        ),
      })}
    >
      <input {...dropzone.getInputProps()} />
      <div className="relative mx-auto flex w-full max-w-[720px] flex-col gap-2 px-4 py-4 min-[761px]:px-6">
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pendingAttachments.map((a) => (
              <AttachmentPill
                key={a.id}
                attachment={a}
                onRemove={() => removeAttachment(a.id)}
              />
            ))}
          </div>
        )}
        <div className="relative">
          <Textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            aria-label="Nachricht"
            disabled={disabled}
            placeholder={
              dropzone.isDragActive
                ? 'Datei hier loslassen…'
                : 'Nachricht schreiben…'
            }
            className="resize-none text-[16px] min-[761px]:text-sm"
          />
          {showPopover && (
            <SlashPopover
              query={slashQuery}
              skills={popoverItems}
              activeIndex={activeIndex}
              onSelect={(skill) => applySkillSelection(skill.skill_name)}
              onActiveIndexChange={setActiveIndex}
            />
          )}
        </div>
        {skillSuggestion && !showPopover && (
          <div className="flex min-w-0 items-center gap-1" role="group" aria-label="Skill-Vorschlag">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="skill-suggestion"
              aria-label={skillSuggestion.label}
              className="h-9 min-w-0 max-w-full truncate"
              disabled={disabled || isStreaming}
              onClick={() => applySkillSuggestion(skillSuggestion.skill)}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{skillSuggestion.label}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label="Vorschlag ausblenden"
              title="Vorschlag ausblenden"
              onClick={() => setDismissedSuggestionFor(value.trim())}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
        <div className="flex items-end justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 min-[761px]:gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 w-11 shrink-0 p-0 min-[761px]:h-9 min-[761px]:w-9"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading || !sessionId}
              title="Datei anhängen"
              aria-label="Datei anhängen"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                if (files.length > 0) void handleFiles(files)
                e.target.value = ''
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'h-11 w-11 shrink-0 p-0 min-[761px]:h-9 min-[761px]:w-9',
                voice.isRecording && 'bg-destructive/15 text-destructive',
              )}
              onClick={() => void handleVoiceToggle()}
              disabled={disabled || isUploading || !sessionId}
              title={voice.isRecording ? 'Aufnahme stoppen + transkribieren' : 'Voice-Aufnahme starten'}
              aria-label={voice.isRecording ? 'Aufnahme stoppen + transkribieren' : 'Voice-Aufnahme starten'}
            >
              {voice.isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <ChatActionMenu onSelect={applySkillSelection} disabled={disabled || isStreaming} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'h-11 min-w-11 gap-1.5 px-2 text-[12px] min-[761px]:h-9 min-[761px]:min-w-9',
                searchVault && 'bg-primary/10 text-primary',
              )}
              onClick={() => setSearchVault((prev) => !prev)}
              disabled={disabled || isStreaming || !sessionId}
              title={searchVault ? 'Quellen-Suche aktiviert' : 'Quellen bei dieser Frage durchsuchen'}
              aria-label={searchVault ? 'Quellen-Suche aktiviert' : 'Quellen bei dieser Frage durchsuchen'}
              aria-pressed={searchVault}
            >
              <Search className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Quellen</span>
            </Button>
            <Select
              value={model}
              onValueChange={onModelChange}
              disabled={disabled || isStreaming || isUpdatingModel || !onModelChange}
            >
              <SelectTrigger
                className="h-11 min-w-0 flex-1 basis-28 gap-1.5 rounded-md bg-muted px-2 py-1 text-[12px] text-muted-foreground min-[761px]:h-9 [&>svg]:hidden"
                aria-label="Modell auswählen"
              >
                <Cpu className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate text-sm">
                  {KNOWN_CHAT_MODELS.find((m) => m.value === model)?.label ?? model}
                </span>
              </SelectTrigger>
              <SelectContent>
                {KNOWN_CHAT_MODELS.filter(
                  (m) => !allowedModels || allowedModels.includes(m.value),
                ).map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            className="h-11 shrink-0 min-[761px]:h-9"
            disabled={disabled || isStreaming || (!value.trim() && pendingAttachments.length === 0)}
            size="sm"
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {isStreaming ? 'Sendet…' : 'Senden'}
          </Button>
        </div>
      </div>

      <Dialog
        open={piiConfirmTypes !== null}
        onOpenChange={(open) => {
          if (!open) setPiiConfirmTypes(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mögliche personenbezogene Daten erkannt</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Dieser Text enthält möglicherweise personenbezogene Daten
            {piiConfirmTypes?.length ? ` (${piiConfirmTypes.join(', ')})` : ''}. Trotzdem senden?
          </p>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPiiConfirmTypes(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => {
                setPiiConfirmTypes(null)
                proceedWithSend(value.trim())
              }}
            >
              Trotzdem senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

function detectLikelyPii(input: string): string[] {
  const types = new Set<string>()
  if (/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i.test(input)) types.add('E-Mail')
  if (/(?:\+|00)?(?:41|49|43)?[\s.-]?(?:\(?0?\d{1,4}\)?[\s.-]?)?\d{2,4}[\s.-]?\d{2,4}[\s.-]?\d{2,6}\b/.test(input)) {
    types.add('Telefon')
  }
  if (/\b[A-Z]{2}\d{2}(?:[\s-]?[A-Z0-9]){11,30}\b/i.test(input)) types.add('IBAN')
  if (/\b756[.\s-]?\d{4}[.\s-]?\d{4}[.\s-]?\d{2}\b/.test(input)) types.add('AHV')
  if (/\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/.test(input)) {
    types.add('IP')
  }
  return [...types]
}

// ---------- Parsers ----------

type SlashState =
  | { kind: 'none' }
  | { kind: 'in_skill_name'; query: string }
  | { kind: 'after_skill_name'; skillName: string; rest: string }

function parseSlashState(value: string): SlashState {
  if (!value.startsWith('/')) return { kind: 'none' }
  const afterSlash = value.slice(1)
  const spaceIdx = afterSlash.indexOf(' ')
  if (spaceIdx === -1) {
    return { kind: 'in_skill_name', query: afterSlash }
  }
  return {
    kind: 'after_skill_name',
    skillName: afterSlash.slice(0, spaceIdx),
    rest: afterSlash.slice(spaceIdx + 1).trim(),
  }
}

function parseSkillCall(
  trimmed: string,
  installed: SkillInstalled[],
): SkillInvocation | null {
  if (!trimmed.startsWith('/')) return null
  const state = parseSlashState(trimmed)
  let skillName: string
  let rest: string
  if (state.kind === 'in_skill_name') {
    skillName = state.query
    rest = ''
  } else if (state.kind === 'after_skill_name') {
    skillName = state.skillName
    rest = state.rest
  } else {
    return null
  }

  if (!skillName) return null
  if (!installed.some((s) => s.skill_name === skillName)) return null

  const restTrimmed = rest.trim()
  if (!restTrimmed) return { skillName }

  const firstSpace = restTrimmed.indexOf(' ')
  if (firstSpace === -1) {
    return { skillName, operation: restTrimmed }
  }
  return {
    skillName,
    operation: restTrimmed.slice(0, firstSpace),
    args: restTrimmed.slice(firstSpace + 1).trim() || undefined,
  }
}
